import React, { useState, useCallback, useRef, useEffect, ChangeEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAlert } from '../../../context/alert';
import { useUploadFileMutation } from '../../../hooks/useFileMutation';
import { useTranslations } from '../../../hooks/useTranslations';
import { Translation } from '../../../types/api/common';
import { CategoryResponse, CreateCategoryRequest } from '../../../types/api/categories';
import { SubcategoryResponse } from '../../../types/api/catalogs';
import { createCategory, editCategory } from '../../../services/categories';
import { fetchLanguages } from "../../../services/lang";
import ModalSheet from '../../basic/ModalSheet';
import LangModal from './LangModal';
import ImageComponent from './ImageComponent';
import { LanguageResponse } from '../../../types/api/lang';
import { Stack, Button, TextInput, Text } from '@primer/react'

interface CategoryModalProps {
    category: CategoryResponse | undefined
    isOpen: boolean;
    onDidSaved: () => void;
    onClose: () => void;
}

export const CategoryModal: React.FC<CategoryModalProps> = ({ category, isOpen, onDidSaved, onClose }) => {

    const [allLangs, setAllLangs] = useState<LanguageResponse[]>([]);
    const [isPending, setIsPending] = useState<boolean>(false)
    const [openLangModal, setLangModal] = React.useState(false)
    const [file, setFile] = useState<File | undefined>(undefined);
    const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);

    const {
        translations,
        setTranslations,
        langs,
        setLangs,
        currentLangIndex,
        setCurrentLangIndex,
        getCurrentFieldValue,
        updateFieldValue,
        isAllLanguagesFilled,
        getRussianOrFirstFieldValue,
        langsDidUpdated
    } = useTranslations()

    const { setSuccess, setError, setInfo } = useAlert();
    const uploadFileMutation = useUploadFileMutation();

    const { mutate: fetchLanguagesRequest, isPending: isLoadingLang } = useMutation({
        mutationFn: fetchLanguages,
        onSuccess: (data) => {
            setAllLangs(data.list);
        },
        onError: (error: any) => {
            setAllLangs([]);
        }
    });

    const { mutate: createCategoryRequest } = useMutation({
        mutationFn: (data: CreateCategoryRequest) => createCategory(data),
        onSuccess: (data) => {
            setIsPending(false);
            onDidSaved()
            onClose()
            setSuccess('Раздел успешно создан')
        },
        onError: (_) => setIsPending(false)
    });

    const { mutate: editCategoryRequest } = useMutation({
        mutationFn: (data: CreateCategoryRequest) => editCategory(category?.id ?? '', data),
        onSuccess: (data) => {
            setIsPending(false);
            onDidSaved()
            onClose()
            setInfo('Изменения сохранены')
        },
        onError: (_) => setIsPending(false)
    });

    const uploadFile = (file: File, onComplete?: (fileId: string) => void) => {
        setIsPending(true)
        const reader = new FileReader();
        reader.readAsDataURL(file);
        uploadFileMutation.mutate(
            { file },
            {
                onSuccess: (data) => {
                    setUploadedFileId(data.id);
                    setIsPending(false);
                    onComplete?.(data.id);
                },
                onError: (error) => {
                    setIsPending(false);
                },
            }
        );
    };

    const saveCategory = (fileId: string | null) => {
        setIsPending(true)
        createCategoryRequest({
            title: getRussianOrFirstFieldValue('title'),
            imageId: uploadedFileId ?? fileId,
            translations: translations
        })
    }

    const updateCategory = (fileId: string | null) => {
        setIsPending(true)
        editCategoryRequest({
            title: getRussianOrFirstFieldValue('title'),
            imageId: uploadedFileId ?? fileId,
            translations: translations
        })
    }

    const trySaveCategory = () => {
        if (translations.length === 0) {
            setError('Информация заполнена не для всех выбранных языков')
            return
        }
        if (file === undefined && uploadedFileId == null) {
            setError('Отсутствует изображение')
            return
        }
        if (isAllLanguagesFilled('title')) {
            if (category) {
                // Update
                if (file) {
                    uploadFile(file, (fileId) => {
                        updateCategory(fileId)
                    });
                } else {
                    updateCategory(uploadedFileId)
                }
            } else {
                // Create
                if (file) {
                    uploadFile(file, (fileId) => {
                        saveCategory(fileId)
                    });
                } else {
                    saveCategory(uploadedFileId)
                }
            }
        } else {
            setError('Информация заполнена не для всех выбранных языков')
        }
    };

    useEffect(() => {
        setTranslations(category?.translations ?? [])
        fetchLanguagesRequest({})
        setUploadedFileId(category?.image?.id ?? null)
    }, [category]);

    useEffect(() => {
        if (category && allLangs) {
            // Если есть translations, используем их
            if (category.translations && category.translations.length > 0) {
                const categoryLangCodes = category.translations.map(el => el.languageIsoCode);
                const selectedLangs = allLangs.filter(lang => categoryLangCodes.includes(lang.isoCode));
                setLangs(selectedLangs);
                setTranslations(category.translations);
            }
            // Если translations пуст, но есть title - считаем что это русский
            else if (category.title) {
                const russianLang = allLangs.find(lang => lang.isoCode === 'ru');
                if (russianLang) {
                    setLangs([russianLang]);
                    setTranslations([{
                        languageIsoCode: 'ru',
                        fields: [{ fieldName: 'title', value: category.title }]
                    }]);
                    setCurrentLangIndex(0);
                }
            }
        } else if (allLangs) {
            const russianLang = allLangs.find(lang => lang.isoCode === 'ru');
            if (russianLang) {
                setLangs([russianLang]);
                setTranslations([{
                    languageIsoCode: 'ru',
                    fields: [{ fieldName: 'title', value: "" }]
                }]);
                setCurrentLangIndex(0);
            }
        }
    }, [allLangs, category]);

    const title = category === undefined ? 'Создать раздел' : 'Редактировать раздел'
    const saveButtonTitle = category === undefined ? 'Создать' : 'Сохранить изменения'

    const imageFileDidSelect = (file: File | undefined) => {
        setFile(file)
        setUploadedFileId(null)
    }

    return (
        <ModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="middle"
            headerSize="regular"
        >
            {({ setTitle }) => (
                <Stack className='mt-[16px]'>
                    <Stack direction="horizontal" gap='condensed' justify='start'>
                        {langs.map((lang, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentLangIndex(index)}
                                className={`flex items-center gap-[4px] px-2 py-[6px] border-[1px] 
                        ${currentLangIndex === index ? "bg-aloix-black hover:bg-gray-800" : "bg-white hover:bg-gray-50 border-border"}
                         rounded-md  h-8`}>
                                <span className={`${currentLangIndex === index ? "text-white" : "text-aloix-black"} font-medium text-xs`}>{lang.titleRu || lang.titleOrigin}</span>
                            </button>
                        ))}
                        <LangModal
                            filterOpened={openLangModal}
                            setFilterOpened={(val) => setLangModal(val)}
                            selectedLangs={langs}
                            setSelectedFilter={langsDidUpdated}
                            onApply={() => { }}
                            onDismiss={() => { }}
                        />
                    </Stack>
                    {langs.length > 0 &&
                        <>
                            <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                                Наименование
                            </Text>
                            <TextInput
                                type="text"
                                inputMode="text"
                                value={getCurrentFieldValue('title')}
                                onChange={(e) => updateFieldValue('title', e.target.value)}
                                maxLength={200}
                                placeholder="Придумайте название раздела"
                                size="medium"
                                contrast={!!(getCurrentFieldValue('title') && getCurrentFieldValue('title').trim())}
                            />
                        </>
                    }
                    <Text as="span" weight='semibold' className='text-[14px] text-aloix-black'>
                        Изображение
                    </Text>

                    <ImageComponent
                        media={category?.image ?? undefined}
                        onFileSelect={imageFileDidSelect}
                    />

                    <Stack direction="horizontal" gap='normal' justify='end'>
                        <div></div>
                        <Button
                            onClick={onClose}
                            size='small'
                            variant="default">
                            Отмена
                        </Button>
                        <Button
                            onClick={trySaveCategory}
                            size='small'
                            variant="primary"
                            loading={isPending}
                            disabled={isPending || translations.length === 0}>
                            {saveButtonTitle}
                        </Button>
                    </Stack>
                </Stack>
            )}
        </ModalSheet>
    );
};